import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';
import CreateSubscription from './CreateSubscription';
import EnvModal from './EnvModal';
import EditCustomer from './EditCustomer';
import SetupChecklist from './SetupChecklist';

// Helper component for displaying information fields
const InfoField = ({ label, value, code = false, url = false, email = false, secret = false, multiline = false, copyable = false }) => {
  const [copied, setCopied] = React.useState(false);
  const [showSecret, setShowSecret] = React.useState(false);

  if (!value && value !== 0) return null;

  const handleCopy = async (textToCopy) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('فشل نسخ البيانات');
    }
  };

  let displayValue = value;
  const isSecret = secret && typeof value === 'string' && value.length > 0;
  if (isSecret && !showSecret) {
    displayValue = '••••••••';
  }

  const content = (
    <>
      {url && value ? (
        <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)', textDecoration: 'none', fontWeight: 500 }}>
          {displayValue} ↗
        </a>
      ) : email && value ? (
        <a href={`mailto:${value}`} style={{ color: 'var(--primary-600)', textDecoration: 'none', fontWeight: 500 }}>
          {displayValue}
        </a>
      ) : code ? (
        <div style={{ position: 'relative' }}>
          <code style={{
            display: 'block',
            fontSize: '0.8125rem',
            fontFamily: 'monospace',
            backgroundColor: 'var(--gray-50)',
            padding: '0.5rem',
            paddingRight: copyable || isSecret ? '2.5rem' : '0.5rem',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--gray-200)',
            wordBreak: 'break-all',
            color: 'var(--gray-900)'
          }}>
            {displayValue}
          </code>
          {(copyable || isSecret) && (
            <div style={{ position: 'absolute', left: '0.5rem', top: '0.5rem', display: 'flex', gap: '0.5rem' }}>
              {isSecret && (
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--gray-300)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    color: 'var(--gray-700)',
                    fontWeight: 500
                  }}
                >
                  {showSecret ? '👁️ إخفاء' : '👁️ عرض'}
                </button>
              )}
              <button
                onClick={() => handleCopy(value)}
                style={{
                  background: copied ? 'var(--success-600)' : 'var(--primary-600)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  color: 'white',
                  fontWeight: 500,
                  transition: 'background-color 0.2s'
                }}
              >
                {copied ? '✓ تم النسخ' : '📋 نسخ'}
              </button>
            </div>
          )}
        </div>
      ) : multiline ? (
        <div style={{ fontSize: '0.875rem', color: 'var(--gray-700)', whiteSpace: 'pre-wrap', lineHeight: '1.6', position: 'relative' }}>
          {displayValue}
          {(copyable || isSecret) && (
            <div style={{ marginTop: '0.5rem' }}>
              {isSecret && (
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="btn btn-secondary btn-sm"
                  style={{ marginLeft: '0.5rem' }}
                >
                  {showSecret ? '👁️ إخفاء' : '👁️ عرض'}
                </button>
              )}
              <button
                onClick={() => handleCopy(value)}
                className={copied ? 'btn btn-success btn-sm' : 'btn btn-primary btn-sm'}
              >
                {copied ? '✓ تم النسخ' : '📋 نسخ'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontWeight: 500, color: 'var(--gray-900)', wordBreak: 'break-word', position: 'relative' }}>
          {displayValue}
          {(copyable || isSecret) && (
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
              {isSecret && (
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="btn btn-secondary btn-sm"
                >
                  {showSecret ? '👁️ إخفاء' : '👁️ عرض'}
                </button>
              )}
              <button
                onClick={() => handleCopy(value)}
                className={copied ? 'btn btn-success btn-sm' : 'btn btn-primary btn-sm'}
              >
                {copied ? '✓ تم النسخ' : '📋 نسخ'}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>
        {label}
      </div>
      {content}
    </div>
  );
};

// Helper component for displaying color fields
const ColorField = ({ label, color, hsl }) => {
  if (!color) return null;

  return (
    <div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{
          width: '50px',
          height: '50px',
          backgroundColor: color,
          borderRadius: 'var(--radius-md)',
          border: '2px solid var(--gray-300)',
          flexShrink: 0,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 600, color: 'var(--gray-900)', marginBottom: '0.25rem' }}>
            {color}
          </div>
          {hsl && (
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', fontFamily: 'monospace' }}>
              HSL: {hsl}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Predefined invoice line templates (description may contain {{domain}} for customer frontend)
const INVOICE_ITEM_TEMPLATES = [
  {
    id: 'plan-basic',
    category: 'plan',
    label: 'اشتراك باقة أساسي',
    description: 'اشتراك باقة أساسي - {{domain}}',
    quantity: 1,
    unitPrice: 0,
  },
  {
    id: 'plan-standard',
    category: 'plan',
    label: 'اشتراك باقة قياسي',
    description: 'اشتراك باقة قياسي - {{domain}}',
    quantity: 1,
    unitPrice: 0,
  },
  {
    id: 'plan-premium',
    category: 'plan',
    label: 'اشتراك باقة مميز',
    description: 'اشتراك باقة مميز - {{domain}}',
    quantity: 1,
    unitPrice: 0,
  },
  {
    id: 'balance-ai',
    category: 'balance',
    label: 'إضافة رصيد AI',
    description: 'إضافة رصيد توليد صور بواسطة AI',
    quantity: 1,
    unitPrice: 0,
  },
  {
    id: 'balance-sms',
    category: 'balance',
    label: 'إضافة رصيد SMS',
    description: 'إضافة رصيد رسائل SMS',
    quantity: 1,
    unitPrice: 0,
  },
  {
    id: 'balance-openai',
    category: 'balance',
    label: 'إضافة رصيد OpenAI',
    description: 'إضافة رصيد ذكاء اصطناعي',
    quantity: 1,
    unitPrice: 0,
  },
];

// ISO 4217 currencies (code, name) for invoice currency dropdown
const CURRENCIES = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'ILS', name: 'Israeli New Shekel' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'QAR', name: 'Qatari Riyal' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'JOD', name: 'Jordanian Dinar' },
  { code: 'LBP', name: 'Lebanese Pound' },
  { code: 'ILS', name: 'Israeli New Shekel' },
  { code: 'IQD', name: 'Iraqi Dinar' },
  { code: 'IRR', name: 'Iranian Rial' },
  { code: 'PKR', name: 'Pakistani Rupee' },
  { code: 'BDT', name: 'Bangladeshi Taka' },
  { code: 'LKR', name: 'Sri Lankan Rupee' },
  { code: 'NPR', name: 'Nepalese Rupee' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'VND', name: 'Vietnamese Dong' },
  { code: 'PLN', name: 'Polish Zloty' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'BGN', name: 'Bulgarian Lev' },
  { code: 'UAH', name: 'Ukrainian Hryvnia' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'PEN', name: 'Peruvian Sol' },
  { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'GHS', name: 'Ghanaian Cedi' },
  { code: 'MAD', name: 'Moroccan Dirham' },
  { code: 'TND', name: 'Tunisian Dinar' },
  { code: 'DZD', name: 'Algerian Dinar' },
  { code: 'XOF', name: 'West African CFA Franc' },
  { code: 'XAF', name: 'Central African CFA Franc' },
  { code: 'AFN', name: 'Afghan Afghani' },
  { code: 'ALL', name: 'Albanian Lek' },
  { code: 'AMD', name: 'Armenian Dram' },
  { code: 'ANG', name: 'Netherlands Antillean Guilder' },
  { code: 'AOA', name: 'Angolan Kwanza' },
  { code: 'AWG', name: 'Aruban Florin' },
  { code: 'AZN', name: 'Azerbaijani Manat' },
  { code: 'BAM', name: 'Bosnia-Herzegovina Convertible Mark' },
  { code: 'BBD', name: 'Barbadian Dollar' },
  { code: 'BIF', name: 'Burundian Franc' },
  { code: 'BMD', name: 'Bermudan Dollar' },
  { code: 'BND', name: 'Brunei Dollar' },
  { code: 'BOB', name: 'Bolivian Boliviano' },
  { code: 'BSD', name: 'Bahamian Dollar' },
  { code: 'BTN', name: 'Bhutanese Ngultrum' },
  { code: 'BWP', name: 'Botswanan Pula' },
  { code: 'BYN', name: 'Belarusian Ruble' },
  { code: 'BZD', name: 'Belize Dollar' },
  { code: 'CDF', name: 'Congolese Franc' },
  { code: 'CRC', name: 'Costa Rican Colón' },
  { code: 'CUP', name: 'Cuban Peso' },
  { code: 'CVE', name: 'Cape Verdean Escudo' },
  { code: 'DJF', name: 'Djiboutian Franc' },
  { code: 'DOP', name: 'Dominican Peso' },
  { code: 'ERN', name: 'Eritrean Nakfa' },
  { code: 'ETB', name: 'Ethiopian Birr' },
  { code: 'FJD', name: 'Fijian Dollar' },
  { code: 'FKP', name: 'Falkland Islands Pound' },
  { code: 'GEL', name: 'Georgian Lari' },
  { code: 'GIP', name: 'Gibraltar Pound' },
  { code: 'GMD', name: 'Gambian Dalasi' },
  { code: 'GNF', name: 'Guinean Franc' },
  { code: 'GTQ', name: 'Guatemalan Quetzal' },
  { code: 'GYD', name: 'Guyanaese Dollar' },
  { code: 'HNL', name: 'Honduran Lempira' },
  { code: 'HRK', name: 'Croatian Kuna' },
  { code: 'HTG', name: 'Haitian Gourde' },
  { code: 'ISK', name: 'Icelandic Króna' },
  { code: 'JMD', name: 'Jamaican Dollar' },
  { code: 'KGS', name: 'Kyrgystani Som' },
  { code: 'KHR', name: 'Cambodian Riel' },
  { code: 'KMF', name: 'Comorian Franc' },
  { code: 'KYD', name: 'Cayman Islands Dollar' },
  { code: 'KZT', name: 'Kazakhstani Tenge' },
  { code: 'LAK', name: 'Laotian Kip' },
  { code: 'LRD', name: 'Liberian Dollar' },
  { code: 'LSL', name: 'Lesotho Loti' },
  { code: 'LYD', name: 'Libyan Dinar' },
  { code: 'MDL', name: 'Moldovan Leu' },
  { code: 'MGA', name: 'Malagasy Ariary' },
  { code: 'MKD', name: 'Macedonian Denar' },
  { code: 'MMK', name: 'Myanma Kyat' },
  { code: 'MNT', name: 'Mongolian Tugrik' },
  { code: 'MOP', name: 'Macanese Pataca' },
  { code: 'MRU', name: 'Mauritanian Ouguiya' },
  { code: 'MUR', name: 'Mauritian Rupee' },
  { code: 'MVR', name: 'Maldivian Rufiyaa' },
  { code: 'MWK', name: 'Malawian Kwacha' },
  { code: 'MZN', name: 'Mozambican Metical' },
  { code: 'NAD', name: 'Namibian Dollar' },
  { code: 'NIO', name: 'Nicaraguan Córdoba' },
  { code: 'PAB', name: 'Panamanian Balboa' },
  { code: 'PGK', name: 'Papua New Guinean Kina' },
  { code: 'PYG', name: 'Paraguayan Guarani' },
  { code: 'RSD', name: 'Serbian Dinar' },
  { code: 'RWF', name: 'Rwandan Franc' },
  { code: 'SBD', name: 'Solomon Islands Dollar' },
  { code: 'SCR', name: 'Seychellois Rupee' },
  { code: 'SDG', name: 'Sudanese Pound' },
  { code: 'SHP', name: 'Saint Helena Pound' },
  { code: 'SLL', name: 'Sierra Leonean Leone' },
  { code: 'SOS', name: 'Somali Shilling' },
  { code: 'SRD', name: 'Surinamese Dollar' },
  { code: 'SSP', name: 'South Sudanese Pound' },
  { code: 'STN', name: 'São Tomé and Príncipe Dobra' },
  { code: 'SYP', name: 'Syrian Pound' },
  { code: 'SZL', name: 'Swazi Lilangeni' },
  { code: 'TJS', name: 'Tajikistani Somoni' },
  { code: 'TMT', name: 'Turkmenistani Manat' },
  { code: 'TOP', name: 'Tongan Paʻanga' },
  { code: 'TTD', name: 'Trinidad and Tobago Dollar' },
  { code: 'TVD', name: 'Tuvaluan Dollar' },
  { code: 'TWD', name: 'New Taiwan Dollar' },
  { code: 'TZS', name: 'Tanzanian Shilling' },
  { code: 'UGX', name: 'Ugandan Shilling' },
  { code: 'UYU', name: 'Uruguayan Peso' },
  { code: 'UZS', name: 'Uzbekistan Som' },
  { code: 'VES', name: 'Venezuelan Bolívar Soberano' },
  { code: 'VUV', name: 'Vanuatu Vatu' },
  { code: 'WST', name: 'Samoan Tala' },
  { code: 'XCD', name: 'East Caribbean Dollar' },
  { code: 'XPF', name: 'CFP Franc' },
  { code: 'YER', name: 'Yemeni Rial' },
  { code: 'ZMW', name: 'Zambian Kwacha' },
  { code: 'ZWL', name: 'Zimbabwean Dollar' },
];

const CustomerDetails = ({ customer, onBack, onRefresh }) => {
  const [customerData, setCustomerData] = useState(customer);
  const [usageLogs, setUsageLogs] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [invoiceModal, setInvoiceModal] = useState({ open: false, mode: 'create', invoice: null });
  const [invoiceForm, setInvoiceForm] = useState({
    dueDate: new Date().toISOString().slice(0, 10),
    items: [{ description: '', quantity: 1, unitPrice: 0 }],
    taxRate: 0,
    currency: 'USD',
    notes: '',
    paymentInfo: '',
  });
  const [defaultPaymentInfo, setDefaultPaymentInfo] = useState('');
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  // When set, show a small form to enter plan (start/end/price) or balance (quantity/price) before adding the line
  const [templatePrompt, setTemplatePrompt] = useState(null);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = React.useRef(null);
  const [templatePromptForm, setTemplatePromptForm] = useState({
    startDate: '',
    endDate: '',
    unitPrice: '',
    quantity: '1',
  });
  const [creditForm, setCreditForm] = useState({
    creditType: 'aiCredits',
    operation: 'add',
    amount: '',
    reference: '',
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [envModal, setEnvModal] = useState({ isOpen: false, title: '', content: '', variant: '' });
  const [editMode, setEditMode] = useState(false);
  const [refreshingOpenAI, setRefreshingOpenAI] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);

  useEffect(() => {
    fetchCustomerDetails();
    fetchUsageLogs();
    fetchTransactions();
    fetchSubscription();
  }, [customer._id]);

  useEffect(() => {
    if (activeTab === 'financials' && customer._id) {
      fetchInvoices();
      fetchFinancialSummary();
    }
  }, [activeTab, customer._id]);

  useEffect(() => {
    const fetchDefaultPaymentInfo = async () => {
      try {
        const data = await apiRequest('/admin/default-payment-info');
        if (data?.success && data.data?.defaultPaymentInfo) setDefaultPaymentInfo(data.data.defaultPaymentInfo);
      } catch (_) {}
    };
    fetchDefaultPaymentInfo();
  }, []);

  // Close template dropdown when clicking outside
  useEffect(() => {
    if (!templateDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) {
        setTemplateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [templateDropdownOpen]);

  const fetchCustomerDetails = async () => {
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}`);
      if (data && data.success) {
        setCustomerData(data.data);
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
    }
  };

  const fetchUsageLogs = async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/usage-logs?limit=50`);
      if (data && data.success) {
        setUsageLogs(data.data);
      }
    } catch (error) {
      console.error('Error fetching usage logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/transactions?limit=50`);
      if (data && data.success) {
        setTransactions(data.data);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/subscription`);
      if (data && data.success) {
        setSubscription(data.data);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(null);
    }
  };

  const fetchInvoices = async () => {
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/invoices?limit=100`);
      if (data && data.success) {
        setInvoices(data.data);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/invoices/summary`);
      if (data && data.success) {
        setFinancialSummary(data.data);
      } else {
        setFinancialSummary(null);
      }
    } catch (error) {
      console.error('Error fetching financial summary:', error);
      setFinancialSummary(null);
    }
  };

  const openCreateInvoice = () => {
    setInvoiceForm({
      dueDate: new Date().toISOString().slice(0, 10),
      items: [{ description: '', quantity: 1, unitPrice: 0 }],
      taxRate: 0,
      currency: 'USD',
      notes: '',
      paymentInfo: '',
    });
    setInvoiceModal({ open: true, mode: 'create', invoice: null });
  };

  const openEditInvoice = (inv) => {
    setInvoiceForm({
      dueDate: inv.dueDate ? new Date(inv.dueDate).toISOString().slice(0, 10) : '',
      items: (inv.items && inv.items.length) ? inv.items.map((i) => ({ description: i.description, quantity: i.quantity, unitPrice: i.unitPrice })) : [{ description: '', quantity: 1, unitPrice: 0 }],
      taxRate: inv.taxRate || 0,
      currency: inv.currency || 'USD',
      notes: inv.notes || '',
      paymentInfo: inv.paymentInfo || '',
      status: inv.status,
      amountPaid: inv.amountPaid ?? 0,
    });
    setInvoiceModal({ open: true, mode: 'edit', invoice: inv });
  };

  const closeInvoiceModal = () => {
    setInvoiceModal({ open: false, mode: 'create', invoice: null });
    setTemplatePrompt(null);
  };

  const addInvoiceLine = () => {
    setInvoiceForm((prev) => ({ ...prev, items: [...prev.items, { description: '', quantity: 1, unitPrice: 0 }] }));
  };

  const getCustomerDomain = () => {
    const url = customerData?.storeFrontendUrl || '';
    if (!url) return '(بدون دومين)';
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  const openTemplatePrompt = (templateId) => {
    setTemplateDropdownOpen(false);
    const template = INVOICE_ITEM_TEMPLATES.find((t) => t.id === templateId);
    if (!template) return;
    const today = new Date().toISOString().slice(0, 10);
    setTemplatePrompt({ templateId, template });
    setTemplatePromptForm({
      startDate: today,
      endDate: '',
      unitPrice: '',
      quantity: '1',
    });
  };

  const closeTemplatePrompt = () => {
    setTemplatePrompt(null);
  };

  const confirmTemplateAdd = (e) => {
    e.preventDefault();
    if (!templatePrompt) return;
    const { templateId, template } = templatePrompt;
    const domain = getCustomerDomain();

    if (template.category === 'plan') {
      const startDate = templatePromptForm.startDate;
      const endDate = templatePromptForm.endDate;
      const unitPrice = Number(templatePromptForm.unitPrice) || 0;
      if (!startDate || !endDate) {
        alert('يرجى إدخال تاريخ البدء وتاريخ الانتهاء');
        return;
      }
      const desc = template.description.replace(/\{\{domain\}\}/g, domain);
      const description = `${desc} (من ${startDate} إلى ${endDate})`;
      setInvoiceForm((prev) => ({
        ...prev,
        items: [...prev.items, { description, quantity: 1, unitPrice }],
      }));
    } else {
      const quantity = Number(templatePromptForm.quantity) || 1;
      const unitPrice = Number(templatePromptForm.unitPrice) || 0;
      const description = template.description;
      setInvoiceForm((prev) => ({
        ...prev,
        items: [...prev.items, { description, quantity, unitPrice }],
      }));
    }
    closeTemplatePrompt();
  };

  const updateInvoiceLine = (index, field, value) => {
    setInvoiceForm((prev) => {
      const next = { ...prev, items: [...prev.items] };
      next.items[index] = { ...next.items[index], [field]: value };
      return next;
    });
  };

  const removeInvoiceLine = (index) => {
    setInvoiceForm((prev) => ({
      ...prev,
      items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== index) : prev.items,
    }));
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    setInvoiceSaving(true);
    try {
      const payload = {
        dueDate: invoiceForm.dueDate,
        items: invoiceForm.items.map((i) => ({
          description: i.description || 'صنف',
          quantity: Number(i.quantity) || 0,
          unitPrice: Number(i.unitPrice) || 0,
        })),
        taxRate: Number(invoiceForm.taxRate) || 0,
        currency: invoiceForm.currency || 'USD',
        notes: invoiceForm.notes || '',
        paymentInfo: invoiceForm.paymentInfo || '',
      };
      const data = await apiRequest(`/admin/customers/${customer._id}/invoices`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (data && data.success) {
        setInvoices((prev) => [data.data, ...prev]);
        fetchFinancialSummary();
        closeInvoiceModal();
      } else {
        alert(data?.message || 'فشل إنشاء الفاتورة');
      }
    } catch (err) {
      alert('حدث خطأ في إنشاء الفاتورة');
    } finally {
      setInvoiceSaving(false);
    }
  };

  const handleUpdateInvoice = async (e) => {
    e.preventDefault();
    if (!invoiceModal.invoice?._id) return;
    setInvoiceSaving(true);
    try {
      const payload = {
        dueDate: invoiceForm.dueDate,
        items: invoiceForm.items.map((i) => ({
          description: i.description || 'صنف',
          quantity: Number(i.quantity) || 0,
          unitPrice: Number(i.unitPrice) || 0,
        })),
        taxRate: Number(invoiceForm.taxRate) || 0,
        currency: invoiceForm.currency || 'USD',
        notes: invoiceForm.notes || '',
        paymentInfo: invoiceForm.paymentInfo != null ? invoiceForm.paymentInfo : undefined,
      };
      if (invoiceForm.status !== undefined) payload.status = invoiceForm.status;
      if (invoiceForm.amountPaid !== undefined) payload.amountPaid = Number(invoiceForm.amountPaid);
      const data = await apiRequest(`/admin/customers/${customer._id}/invoices/${invoiceModal.invoice._id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (data && data.success) {
        setInvoices((prev) => prev.map((inv) => (inv._id === data.data._id ? data.data : inv)));
        fetchFinancialSummary();
        closeInvoiceModal();
      } else {
        alert(data?.message || 'فشل تحديث الفاتورة');
      }
    } catch (err) {
      alert('حدث خطأ في تحديث الفاتورة');
    } finally {
      setInvoiceSaving(false);
    }
  };

  const handleMarkInvoicePaid = async (inv) => {
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/invoices/${inv._id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'paid', amountPaid: inv.total, paidAt: new Date().toISOString() }),
      });
      if (data && data.success) {
        setInvoices((prev) => prev.map((i) => (i._id === inv._id ? data.data : i)));
        fetchFinancialSummary();
      }
    } catch (err) {
      alert('حدث خطأ في تحديث الفاتورة');
    }
  };

  const handleCancelInvoice = async (inv) => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذه الفاتورة؟')) return;
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/invoices/${inv._id}`, { method: 'DELETE' });
      if (data && data.success) {
        setInvoices((prev) => prev.map((i) => (i._id === inv._id ? data.data : i)));
        fetchFinancialSummary();
      }
    } catch (err) {
      alert('حدث خطأ في إلغاء الفاتورة');
    }
  };

  const handleStatusChange = async (inv, newStatus) => {
    if (inv.status === newStatus) return;
    try {
      const payload = { status: newStatus };
      if (newStatus === 'paid') {
        payload.amountPaid = inv.total;
        payload.paidAt = new Date().toISOString();
      }
      const data = await apiRequest(`/admin/customers/${customer._id}/invoices/${inv._id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (data && data.success) {
        setInvoices((prev) => prev.map((i) => (i._id === inv._id ? data.data : i)));
        fetchFinancialSummary();
      }
    } catch (err) {
      alert('حدث خطأ في تحديث الحالة');
    }
  };

  const printInvoice = (inv) => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const paymentText = (inv.paymentInfo && String(inv.paymentInfo).trim()) ? inv.paymentInfo : (defaultPaymentInfo || '');
    const itemsRows = (inv.items || []).map((row) => `
      <tr>
        <td class="item-desc">${escapeHtml(row.description || '')}</td>
        <td class="item-num">${Number(row.quantity)}</td>
        <td class="item-num">${Number(row.unitPrice).toLocaleString('en-US')}</td>
        <td class="item-total">${Number(row.amount || row.quantity * row.unitPrice).toLocaleString('en-US')}</td>
      </tr>`).join('');
    const html = `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="utf-8">
  <title>فاتورة ${escapeHtml(inv.invoiceNumber)}</title>
  <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap" rel="stylesheet">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Tajawal', sans-serif; color: #0f172a; font-size: 12px; line-height: 1.45; padding: 0.9rem 1rem; max-width: 100%; }
    .logo { height: 38px; width: auto; display: block; margin-bottom: 0.75rem; }
    .header-row { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 1rem; margin-bottom: 0.9rem; padding-bottom: 0.6rem; border-bottom: 2px solid #0f172a; }
    .bill-to { flex: 1; min-width: 180px; }
    .bill-to-label { font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; margin-bottom: 0.2rem; }
    .bill-to-name { font-size: 1rem; font-weight: 700; color: #0f172a; margin-bottom: 0.15rem; }
    .bill-to-line { color: #475569; font-size: 0.8rem; }
    .inv-meta { text-align: left; }
    .inv-number { font-size: 1.25rem; font-weight: 800; color: #0f172a; margin-bottom: 0.35rem; letter-spacing: -0.02em; }
    .inv-meta-line { font-size: 0.75rem; color: #475569; margin-bottom: 0.1rem; }
    .inv-status { display: inline-block; margin-top: 0.25rem; padding: 0.15rem 0.45rem; font-size: 0.65rem; font-weight: 700; border-radius: 4px; }
    .inv-status.paid { background: #dcfce7; color: #166534; }
    .inv-status.cancelled { background: #fee2e2; color: #991b1b; }
    .inv-status.overdue { background: #fef3c7; color: #92400e; }
    .inv-status.draft, .inv-status.sent { background: #e0f2fe; color: #0369a1; }
    .table-wrap { margin: 0.6rem 0; overflow: hidden; border: 1px solid #e2e8f0; border-radius: 6px; }
    .items-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
    .items-table th { background: #f8fafc; padding: 0.4rem 0.6rem; text-align: right; font-weight: 700; color: #334155; border-bottom: 1px solid #e2e8f0; }
    .items-table td { padding: 0.4rem 0.6rem; border-bottom: 1px solid #f1f5f9; }
    .item-desc { color: #0f172a; font-weight: 500; }
    .item-num { text-align: center; color: #475569; }
    .item-total { text-align: center; font-weight: 700; color: #0f172a; }
    .items-table tbody tr:last-child td { border-bottom: none; }
    .totals-wrap { margin-top: 0.75rem; display: flex; justify-content: flex-start; }
    .totals-table { width: 260px; border-collapse: collapse; font-size: 0.85rem; }
    .totals-table td { padding: 0.25rem 0.5rem; border-bottom: 1px solid #e2e8f0; color: #475569; }
    .totals-table .grand-total td { padding: 0.45rem 0.5rem; font-size: 1rem; font-weight: 800; color: #0f172a; background: #f8fafc; border-bottom: none; border-radius: 0 0 6px 6px; }
    .payment-section { margin-top: 1rem; padding: 0.75rem 1rem; background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 8px; }
    .payment-section h3 { font-size: 0.65rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; margin-bottom: 0.5rem; }
    .payment-content { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
    .bop-logo-wrap { background: #fff; padding: 0.5rem 0.75rem; border-radius: 6px; border: 1px solid #e2e8f0; box-shadow: 0 1px 2px rgba(0,0,0,0.05); flex-shrink: 0; }
    .bop-logo { height: 40px; width: auto; display: block; }
    .payment-details { flex: 1; min-width: 200px; white-space: pre-wrap; font-size: 0.8rem; color: #1e293b; line-height: 1.6; font-weight: 600; }
    .notes-section { margin-top: 0.75rem; padding-top: 0.6rem; border-top: 1px solid #e2e8f0; }
    .notes-section h3 { font-size: 0.65rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; margin-bottom: 0.25rem; }
    .notes-section p { color: #475569; white-space: pre-wrap; font-size: 0.8rem; }
    .thank-you { margin-top: 1rem; padding: 0.6rem 0.5rem; text-align: center; font-size: 0.9rem; font-weight: 700; color: #0f172a; line-height: 1.5; }
    .thank-you .line1 { display: block; margin-bottom: 0.2rem; }
    .thank-you .line2 { display: block; font-size: 0.8rem; font-weight: 500; color: #475569; }
    .invoice-footer { margin-top: 2rem; background: #0f172a; color: #e2e8f0; padding: 1.5rem 1.75rem; border-radius: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .invoice-footer-accent { height: 3px; background: linear-gradient(90deg, #0369a1 0%, #0ea5e9 100%); margin: -1.5rem -1.75rem 1.25rem -1.75rem; border-radius: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .invoice-footer-grid { display: grid; grid-template-columns: 1fr auto; gap: 2rem; align-items: start; }
    .invoice-footer-brand { text-align: right; direction: rtl; }
    .invoice-footer-brand .footer-logo-wrap { display: inline-block; background: #fff; padding: 0.5rem 0.75rem; border-radius: 6px; margin-bottom: 0.6rem; }
    .invoice-footer-brand .footer-logo { height: 36px; width: auto; display: block; vertical-align: middle; }
    .invoice-footer-brand .footer-company { font-size: 1.25rem; font-weight: 800; color: #fff; letter-spacing: -0.03em; margin-bottom: 0.25rem; line-height: 1.2; }
    .invoice-footer-brand .footer-slogan { font-size: 0.75rem; color: #94a3b8; font-weight: 500; line-height: 1.5; letter-spacing: 0.01em; max-width: 280px; }
    .invoice-footer-contact { text-align: right; direction: rtl; }
    .invoice-footer-contact .footer-contact-title { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #94a3b8; margin-bottom: 0.75rem; }
    .invoice-footer-contact .footer-contact-list { display: flex; flex-direction: column; gap: 0.5rem; align-items: flex-end; }
    .invoice-footer-contact .footer-contact-item { display: flex; align-items: center; gap: 0.5rem; font-size: 0.8rem; flex-direction: row-reverse; }
    .invoice-footer-contact .footer-contact-item svg { width: 16px; height: 16px; flex-shrink: 0; color: #94a3b8; }
    .invoice-footer-contact .footer-contact-item a { color: #e2e8f0; text-decoration: none; font-weight: 600; }
    .invoice-footer-contact .footer-contact-item a:hover { color: #38bdf8; }
    .invoice-footer-bottom { margin-top: 1.25rem; padding-top: 1rem; border-top: 1px solid rgba(148,163,184,0.25); font-size: 0.7rem; color: #64748b; text-align: center; direction: rtl; }
    @media (max-width: 520px) { .invoice-footer-grid { grid-template-columns: 1fr; } }
    .no-print { margin-top: 1rem; }
    .no-print button { font-family: 'Tajawal', sans-serif; padding: 0.5rem 1.25rem; font-size: 0.9rem; font-weight: 600; cursor: pointer; border: none; border-radius: 6px; margin-left: 0.5rem; }
    .no-print .btn-print { background: #0f172a; color: #fff; }
    .no-print .btn-close { background: #64748b; color: #fff; }
    @media print { body { padding: 0.6rem 0.75rem; font-size: 11px; } .no-print { display: none !important; } .invoice-footer, .invoice-footer-accent { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
  </style>
</head>
<body>
  <img src="${baseUrl}/logo.png" alt="Tijara+" class="logo" />
  <div class="header-row">
    <div class="bill-to">
      <div class="bill-to-label">جهة الفواتير / العميل</div>
      <div class="bill-to-name">${escapeHtml(customerData?.name || '')}</div>
      ${customerData?.email ? `<div class="bill-to-line">${escapeHtml(customerData.email)}</div>` : ''}
      ${customerData?.phone ? `<div class="bill-to-line">${escapeHtml(customerData.phone)}</div>` : ''}
    </div>
    <div class="inv-meta">
      <div class="inv-number">فاتورة # ${escapeHtml(inv.invoiceNumber)}</div>
      <div class="inv-meta-line">تاريخ الإصدار: ${inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('en-US') : '—'}</div>
      <div class="inv-meta-line">تاريخ الاستحقاق: ${inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-US') : '—'}</div>
      <span class="inv-status ${inv.status}">${statusLabel(inv.status)}</span>
    </div>
  </div>
  <div class="table-wrap">
    <table class="items-table">
      <thead>
        <tr>
          <th style="text-align:right;">الوصف</th>
          <th style="text-align:center;">الكمية</th>
          <th style="text-align:center;">سعر الوحدة</th>
          <th style="text-align:center;">المبلغ</th>
        </tr>
      </thead>
      <tbody>${itemsRows}</tbody>
    </table>
  </div>
  <div class="totals-wrap">
    <table class="totals-table">
      <tr><td>المجموع الفرعي</td><td style="text-align:left;font-weight:600">${(inv.subtotal ?? 0).toLocaleString('en-US')} ${inv.currency || 'USD'}</td></tr>
      ${(inv.taxRate > 0) ? `<tr><td>الضريبة (${inv.taxRate}%)</td><td style="text-align:left;font-weight:600">${(inv.taxAmount ?? 0).toLocaleString('en-US')} ${inv.currency || 'USD'}</td></tr>` : ''}
      <tr class="grand-total"><td>الإجمالي</td><td style="text-align:left">${(inv.total ?? 0).toLocaleString('en-US')} ${inv.currency || 'USD'}</td></tr>
    </table>
  </div>
  ${paymentText ? `
  <div class="payment-section">
    <h3>معلومات الدفع — بنك فلسطين</h3>
    <div class="payment-content">
      <div class="bop-logo-wrap">
        <img src="${baseUrl}/bop.png" alt="بنك فلسطين" class="bop-logo" />
      </div>
      <div class="payment-details">${escapeHtml(paymentText)}</div>
    </div>
  </div>` : ''}
  ${inv.notes && String(inv.notes).trim() ? `<div class="notes-section"><h3>ملاحظات</h3><p>${escapeHtml(inv.notes)}</p></div>` : ''}
  <div class="thank-you">
    <span class="line1">نشكركم على ثقتكم وتعاونكم.</span>
    <span class="line2">نتطلع إلى استمرار شراكتنا معكم — لأي استفسار نحن هنا لمساعدتكم.</span>
  </div>
  <footer class="invoice-footer">
    <div class="invoice-footer-accent"></div>
    <div class="invoice-footer-grid">
      <div class="invoice-footer-brand">
        <div class="footer-logo-wrap"><img src="${baseUrl}/logo.png" alt="Tijara+" class="footer-logo" /></div>
        <div class="footer-company">Tijara+</div>
        <div class="footer-slogan">منصتك الاحترافية لإدارة المتاجر والتجارة الإلكترونية مدعومة بالذكاء الاصطناعي</div>
      </div>
      <div class="invoice-footer-contact">
        <div class="footer-contact-title">معلومات الاتصال</div>
        <div class="footer-contact-list">
          <div class="footer-contact-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            <a href="https://tijarapl.us" target="_blank" rel="noopener">tijarapl.us</a>
          </div>
          <div class="footer-contact-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            <a href="mailto:info@tijarapl.us">info@tijarapl.us</a>
          </div>
          <div class="footer-contact-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            <a style="direction: ltr;" href="tel:+972599513387">+972 59 951 3387</a>
          </div>
         
        </div>
      </div>
    </div>
    <div class="invoice-footer-bottom">هذه الفاتورة صادرة عن Tijara+ — جميع الحقوق محفوظة</div>
  </footer>
  <p class="no-print"><button onclick="window.print()" class="btn-print">طباعة</button><button onclick="window.close()" class="btn-close">إغلاق</button></p>
</body>
</html>`;
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  };

  const escapeHtml = (str) => {
    if (str == null) return '';
    const s = String(str);
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  const statusLabel = (s) => {
    const map = { draft: 'مسودة', sent: 'مرسلة', paid: 'مدفوعة', overdue: 'متأخرة', cancelled: 'ملغاة' };
    return map[s] || s;
  };

  const generateBackendEnv = () => {
    const c = customerData;
    const apiPublicUrl = c.apiUrl || '';
    const r2PublicUrl = c.r2PublicUrl || '';
    const r2PublicBaseUrl = c.r2PublicBaseUrl || r2PublicUrl;
    const mongoUri = c.mongodbUri || '';
    const mongoDbName = c.mongodbDbName || '';
    const jwtSecret = c.jwtSecret || '';
    const jwtExpire = c.jwtExpire || '7d';
    const storeFrontendUrl = c.storeFrontendUrl || '';
    const aiImageCallbackUrl = c.aiImageCallbackUrl || (apiPublicUrl ? `${apiPublicUrl.replace(/\/+$/,'')}/api/ai-image/callback` : '');
    const nanoBananaBaseUrl = 'https://crmapi.tijarapl.us';
    const balanceApiBaseUrl = 'https://crmapi.tijarapl.us';
    const FACEBOOK_SERVICE_URL = 'https://crmapi.tijarapl.us';
    const mdbConn = c.mdbMcpConnectionString || mongoUri;
    const mdbDb = c.mdbMcpDatabaseName || mongoDbName;

    let env = `NODE_ENV=development
PORT=${c.port || ''}
API_PUBLIC_URL=${apiPublicUrl}
MONGODB_URI=${mongoUri}
MONGODB_DB_NAME=${mongoDbName}
JWT_SECRET=${jwtSecret}
JWT_EXPIRE=${jwtExpire}
STORE_FRONTEND_URL=${storeFrontendUrl}
R2_ACCESS_KEY_ID=${c.r2AccessKeyId || ''}
R2_SECRET_ACCESS_KEY=${c.r2SecretAccessKey || ''}
R2_BUCKET_NAME=${c.r2BucketName || ''}
R2_PUBLIC_URL=${r2PublicUrl}
R2_ENDPOINT=${c.r2Endpoint || ''}
R2_PUBLIC_BASE_URL=${r2PublicBaseUrl}
QDRANT_URL=http://localhost:6333
QDRANT_COLLECTION_NAME=${c.qdrantCollectionName || ''}
SMTP_HOST=smtp-relay.brevo.com
SMTP_PORT=587
SMTP_SECURE=false
SMTP_USER=your-email@smtp-brevo.com
SMTP_PASSWORD=your-smtp-password
EMAIL_FROM_NAME=Tijara Plus
EMAIL_FROM_ADDRESS=noreply@tijarapl.us
TELEGRAM_BOT_TOKEN=${c.telegramBotToken || ''}
TELEGRAM_BOT_USERNAME=${c.telegramBotUsername || ''}
TELEGRAM_WEBHOOK_PATH=/telegram/webhook
TELEGRAM_DOC_TOKEN_SECRET=${jwtSecret}
TELEGRAM_DOC_TOKEN_TTL=${jwtExpire}
TELEGRAM_LINK_SECRET=${jwtSecret}
TELEGRAM_LINK_TTL=${jwtExpire}
NANOBANANA_API_KEY=${c.apiToken || ''}
NANOBANANA_API_BASE_URL=${nanoBananaBaseUrl}
AI_IMAGE_CALLBACK_URL=${aiImageCallbackUrl}
BALANCE_API_BASE_URL=${balanceApiBaseUrl}
BALANCE_API_TOKEN=${c.apiToken || ''}
OPENAI_API_KEY=${c.openaiApiKey || ''}
ADMIN_DEFAULT_EMAIL=${c.adminDefaultEmail || ''}
ADMIN_DEFAULT_PASSWORD=${c.adminDefaultPassword || ''}
ADMIN_DEFAULT_NAME=${c.adminDefaultName || ''}
ADMIN_DEFAULT_PHONE=${c.adminDefaultPhone || ''}
TELEGRAM_NOTIFICATION_TOKEN=${c.telegramNotificationToken || ''}
MDB_MCP_CONNECTION_STRING=${mdbConn}
MDB_MCP_DATABASE_NAME=${mdbDb}
OPENAI_MODEL=${c.openaiModel || 'gpt-4.1-nano-2025-04-14'}
AI_AGENT_MAX_ITERATIONS=${c.aiAgentMaxIterations || 50}
FACEBOOK_SERVICE_URL=${FACEBOOK_SERVICE_URL}
FACEBOOK_CUSTOMER_API_TOKEN=${c.apiToken || ''}
`;

    return env;
  };

  const generateFrontendEnv = () => {
    const c = customerData;
    const storeName = c.name || 'Store';
    const apiBaseUrl = c.apiUrl || 'https://api.example.com';
    const r2PublicUrl = c.r2PublicUrl || '';
    
    let env = `# ============================================
# ${storeName} Frontend Environment Variables
# ============================================
VITE_API_URL=${apiBaseUrl}
VITE_API_BASE_URL=${apiBaseUrl}/api
VITE_STORE_FRONTEND_URL=${c.storeFrontendUrl || 'http://localhost:8080'}
VITE_R2_PUBLIC_URL=${r2PublicUrl}
`;

    return env;
  };

  const handleShowBackendEnv = () => {
    const envContent = generateBackendEnv();
    setEnvModal({
      isOpen: true,
      title: 'ملف .env للخادم (Backend)',
      content: envContent,
      variant: ''
    });
  };

  const handleShowFrontendEnv = () => {
    const envContent = generateFrontendEnv();
    setEnvModal({
      isOpen: true,
      title: 'ملف .env للواجهة الأمامية (Frontend)',
      content: envContent,
      variant: ''
    });
  };

  const generateAgentBackendEnv = () => {
    const c = customerData;
    const agentPort = c.agentPort || '';
    const agentMongo = c.agentMongodbUri || '';
    const jwtSecret = c.jwtSecret || '';
    const webhookSecret = c.webhookAppSecret || '';
    const replayWindow = c.webhookReplayWindowSeconds ?? 300;
    const openaiModel = c.openaiModel || 'gpt-4.1-nano-2025-04-14';
    const storeName = c.name || '';
    const storeNameAr = c.storeNameAr || '';
    const storeAddress = c.storeAddress || '';
    const storeEmail = c.adminDefaultEmail || c.email || '';
    const storePhone = c.adminDefaultPhone || c.phone || '';
    const storeFacebook = c.storeFacebook || '';
    const storeInstagram = c.storeInstagram || '';
    const storeWebsite = c.storeFrontendUrl || '';
    const storeType = c.storeType || '';
    const adminEmail = c.adminDefaultEmail || '';
    const adminPassword = c.adminDefaultPassword || '';
    const adminName = c.adminDefaultName || '';
    const notificationBaseUrl = c.apiUrl || '';
    const notificationToken = c.telegramNotificationToken || '';
    const aiAgentFrontend = c.aiAgentFrontendUrl || '';
    const sessionDomain = c.sessionEmailDomain || '';
    const crmBase = 'https://crmapi.tijarapl.us';

    return `# Agent Backend .env (AI Agent – different port/DB than store)
# Server Configuration
PORT=${agentPort}
NODE_ENV=development
CORS_ORIGINS=*

# MongoDB Configuration (agent DB)
MONGODB_URI=${agentMongo}

# JWT Authentication (same as store)
JWT_SECRET=${jwtSecret}

# Webhook Security (X-Hub-Signature-256)
WEBHOOK_APP_SECRET=${webhookSecret}
WEBHOOK_REPLAY_WINDOW_SECONDS=${replayWindow}

# OpenAI Configuration (same as store)
OPENAI_API_KEY=${c.openaiApiKey || ''}
OPENAI_MODEL=${openaiModel}

# OpenAI Balance API
OPENAI_BALANCE_API_URL=${crmBase}/api/openai/balance
OPENAI_BALANCE_API_TOKEN=${c.apiToken || ''}

# TijaraPlus API (Facebook Messenger & Instagram)
TIJARAPLUS_API_URL=${crmBase}/api
TIJARAPLUS_CUSTOMER_API_TOKEN=${c.apiToken || ''}

# Ecommerce API
ECOMMERCE_API_URL=${c.ecommerceApiUrl || ''}

# Store Information
STORE_NAME=${storeName}
STORE_NAME_AR=${storeNameAr}
STORE_ADDRESS=${storeAddress}
STORE_EMAIL=${storeEmail}
STORE_PHONE=${storePhone}
STORE_FACEBOOK=${storeFacebook}
STORE_INSTAGRAM=${storeInstagram}
STORE_WEBSITE=${storeWebsite}
STORE_TYPE=${storeType}

# Admin Configuration (same as store)
ADMIN_EMAIL=${adminEmail}
ADMIN_PASSWORD=${adminPassword}
ADMIN_NAME=${adminName}

# Cloudflare R2 (same as store)
R2_ACCESS_KEY_ID=${c.r2AccessKeyId || ''}
R2_SECRET_ACCESS_KEY=${c.r2SecretAccessKey || ''}
R2_BUCKET_NAME=${c.r2BucketName || ''}
R2_PUBLIC_URL=${c.r2PublicUrl || ''}
R2_ENDPOINT=${c.r2Endpoint || ''}

# Balance API (AI balance display)
BALANCE_API_BASE_URL=${crmBase}
BALANCE_API_TOKEN=${c.apiToken || ''}

# Admin Notification (Telegram)
NOTIFICATION_API_BASE_URL=${notificationBaseUrl}
NOTIFICATION_API_TOKEN=${notificationToken}

# AI Agent Frontend URL (chat links in notifications)
AI_AGENT_FRONTEND_URL=${aiAgentFrontend}

# Session User Email Domain
SESSION_EMAIL_DOMAIN=${sessionDomain}
`;
  };

  const generateAgentFrontendEnv = () => {
    const c = customerData;
    const agentApiUrl = (c.agentApiUrl || '').replace(/\/+$/, '');
    const base = agentApiUrl ? `${agentApiUrl}/api` : '';
    return `# Agent Frontend .env (AI Agent chat UI)
REACT_APP_API_ENDPOINT=${base}
REACT_APP_API_URL=${base}
`;
  };

  const handleShowAgentBackendEnv = () => {
    setEnvModal({
      isOpen: true,
      title: 'ملف .env لخادم الوكيل (Agent Backend)',
      content: generateAgentBackendEnv(),
      variant: ''
    });
  };

  const handleShowAgentFrontendEnv = () => {
    setEnvModal({
      isOpen: true,
      title: 'ملف .env لواجهة الوكيل (Agent Frontend)',
      content: generateAgentFrontendEnv(),
      variant: ''
    });
  };

  const generateWhatsAppMessage = () => {
    const c = customerData;
    const storeFront = (c.storeFrontendUrl || '').replace(/\/+$/, '');
    const adminLoginUrl = storeFront ? `${storeFront}/admin/login` : '';
    const adminEmail = c.adminDefaultEmail || '';
    const adminPassword = c.adminDefaultPassword || '';
    const telegramAdminBotUsername = (c.telegramBotUsername || '').trim();
    const telegramAdminLink = telegramAdminBotUsername
      ? `https://t.me/${telegramAdminBotUsername.replace(/^@/, '')}`
      : '';
    const telegramCustomerBotUsername = telegramAdminBotUsername
      ? telegramAdminBotUsername.replace(/_admin/gi, '')
      : '';
    const telegramCustomerLink = telegramCustomerBotUsername
      ? `https://t.me/${telegramCustomerBotUsername.replace(/^@/, '')}`
      : '';
    let agentPanelUrl = '';
    if (storeFront) {
      try {
        const u = new URL(storeFront);
        u.hostname = 'agent.' + u.hostname;
        u.pathname = '/';
        u.search = '';
        u.hash = '';
        agentPanelUrl = u.toString();
      } catch (_) {
        agentPanelUrl = '';
      }
    }
    const lines = [
      'مرحباً،',
      'إليك بيانات الدخول والروابط للمتجر:',
      '',
      storeFront ? `🔗 رابط المتجر: ${storeFront}` : '',
      adminLoginUrl ? `🔐 تسجيل دخول الإدارة: ${adminLoginUrl}` : '',
      adminEmail ? `📧 البريد الافتراضي للإدارة: ${adminEmail}` : '',
      adminPassword ? `🔑 كلمة المرور الافتراضية: ${adminPassword}` : '',
      agentPanelUrl ? `🤖 لوحة وكيل الذكاء الاصطناعي: ${agentPanelUrl}` : '',
      telegramAdminLink ? `📱 بوت تيليجرام للأدمن: ${telegramAdminLink}` : '',
      telegramCustomerLink ? `📱 بوت تيليجرام للعملاء: ${telegramCustomerLink}` : '',
      '',
      '⚠️ يرجى تغيير كلمة المرور عند أول تسجيل دخول.',
    ].filter(Boolean);
    return lines.join('\n');
  };

  const handleShowWhatsAppMessage = () => {
    const message = generateWhatsAppMessage();
    setEnvModal({
      isOpen: true,
      title: 'رسالة واتساب للعميل',
      content: message,
      variant: 'whatsapp'
    });
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('هل أنت متأكد من إعادة إنشاء رمز API؟ الرمز الحالي سيتوقف عن العمل.')) {
      return;
    }

    try {
      const data = await apiRequest(
        `/admin/customers/${customer._id}/regenerate-token`,
        { method: 'POST' }
      );
      if (data.success) {
        alert(`تم إنشاء رمز API جديد:\n\n${data.data.apiToken}\n\nاحفظ هذا الرمز في مكان آمن.`);
        fetchCustomerDetails();
      } else {
        alert('حدث خطأ: ' + data.message);
      }
    } catch (error) {
      alert('حدث خطأ في الاتصال بالخادم');
    }
  };

  const handleCopyApiKey = () => {
    const apiToken = customerData.apiToken || customer.apiToken;
    if (apiToken) {
      navigator.clipboard.writeText(apiToken).then(() => {
        setApiKeyCopied(true);
        setTimeout(() => setApiKeyCopied(false), 2000);
      }).catch(err => {
        console.error('Failed to copy API key:', err);
        alert('فشل نسخ رمز API');
      });
    }
  };

  const handleRefreshOpenAICosts = async () => {
    setRefreshingOpenAI(true);
    try {
      const data = await apiRequest(`/admin/openai/customers/${customer._id}/refresh`, {
        method: 'POST',
        body: JSON.stringify({ daysBack: 180 }),
      });
      if (data.success) {
        setMessage({ type: 'success', text: data.message || 'تم تحديث تكاليف OpenAI بنجاح' });
        fetchCustomerDetails();
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
    } finally {
      setRefreshingOpenAI(false);
    }
  };

  const handleAdjustCredits = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/credits`, {
        method: 'POST',
        body: JSON.stringify(creditForm),
      });
      if (data.success) {
        setMessage({ type: 'success', text: 'تم تعديل الرصيد بنجاح' });
        setCreditForm({ ...creditForm, amount: '', reference: '' });
        fetchCustomerDetails();
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
    }
  };

  if (editMode) {
    return (
      <EditCustomer
        customer={customerData}
        onUpdate={(updatedCustomer) => {
          setCustomerData(updatedCustomer);
          setEditMode(false);
          if (onRefresh) onRefresh();
        }}
        onCancel={() => setEditMode(false)}
      />
    );
  }

  const apiToken = customerData.apiToken || customer.apiToken;

  return (
    <div>
      {/* Header Section with Customer Info */}
      <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-800) 100%)', color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1.5rem' }}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.3)', color: 'white' }}>
                ← رجوع
              </button>
            </div>
            <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', color: 'white' }}>
              {customerData.name || 'بدون اسم'}
            </h1>
            <p style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '1rem' }}>
              {customerData.email}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span className={`badge ${customerData.isActive ? 'badge-success' : 'badge-danger'}`} style={{ backgroundColor: customerData.isActive ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
                {customerData.isActive ? '✓ نشط' : '✗ غير نشط'}
              </span>
              {customerData.businessType && (
                <span className="badge" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
                  {customerData.businessType === 'electronics' ? 'إلكترونيات' :
                   customerData.businessType === 'clothes' ? 'ملابس' :
                   customerData.businessType === 'home-accessories' ? 'إكسسوارات منزلية' : customerData.businessType}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <button className="btn btn-primary btn-sm" onClick={() => setEditMode(true)} style={{ backgroundColor: 'white', color: 'var(--primary-600)', border: 'none' }}>
              ✏️ تعديل
            </button>
            {apiToken && (
              <button className="btn btn-primary btn-sm" onClick={handleCopyApiKey} style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.3)', color: 'white' }}>
                {apiKeyCopied ? '✓ تم النسخ' : '📋 نسخ API Key'}
              </button>
            )}
            {customerData.openaiProjectId && (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleRefreshOpenAICosts}
                disabled={refreshingOpenAI}
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.3)', color: 'white' }}
              >
                {refreshingOpenAI ? '⏳ جاري التحديث...' : '🔄 تحديث OpenAI'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Balance Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>رصيد AI</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary-600)' }}>
            {customerData.balances?.aiCredits?.toLocaleString() || '0'}
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>رصيد SMS</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--info)' }}>
            {customerData.balances?.smsCredits?.toLocaleString() || '0'}
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>رصيد OpenAI</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--success)' }}>
            {customerData.balances?.openaiBalance?.toLocaleString() || '0'}
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '1.5rem' }}>
          <span>{message.type === 'success' ? '✅' : '❌'}</span>
          <span>{message.text}</span>
        </div>
      )}

      {/* Credit Adjustment Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">تعديل الرصيد</h3>
        </div>
        <form onSubmit={handleAdjustCredits}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group">
              <label className="form-label">نوع العملية</label>
              <select
                className="form-control"
                value={creditForm.operation}
                onChange={(e) => setCreditForm({ ...creditForm, operation: e.target.value })}
              >
                <option value="add">➕ إضافة رصيد</option>
                <option value="deduct">➖ خصم رصيد</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">نوع الرصيد</label>
              <select
                className="form-control"
                value={creditForm.creditType}
                onChange={(e) => setCreditForm({ ...creditForm, creditType: e.target.value })}
              >
                <option value="aiCredits">رصيد AI ({customerData.balances?.aiCredits || 0})</option>
                <option value="smsCredits">رصيد SMS ({customerData.balances?.smsCredits || 0})</option>
                <option value="openaiBalance">رصيد OpenAI ({customerData.balances?.openaiBalance || 0})</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">المبلغ</label>
              <input
                type="number"
                className="form-control"
                value={creditForm.amount}
                onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                min="0"
                step="0.01"
                required
                placeholder="أدخل المبلغ"
              />
            </div>
            <div className="form-group">
              <label className="form-label">المرجع (اختياري)</label>
              <input
                type="text"
                className="form-control"
                value={creditForm.reference}
                onChange={(e) => setCreditForm({ ...creditForm, reference: e.target.value })}
                placeholder="مثال: دفع فاتورة"
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button 
              type="submit" 
              className={`btn ${creditForm.operation === 'add' ? 'btn-success' : 'btn-danger'}`}
            >
              {creditForm.operation === 'add' ? 'إضافة رصيد' : 'خصم رصيد'}
            </button>
          </div>
        </form>
      </div>

      {/* Main Content Tabs */}
      <div className="card">
        <div className="tabs">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 نظرة عامة
          </button>
          <button
            className={`tab-button ${activeTab === 'subscription' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscription')}
          >
            📋 الاشتراك
          </button>
          <button
            className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📝 سجل الاستخدام
          </button>
          <button
            className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            💳 المعاملات
          </button>
          <button
            className={`tab-button ${activeTab === 'financials' ? 'active' : ''}`}
            onClick={() => setActiveTab('financials')}
          >
            💰 المالية والفواتير
          </button>
          <button
            className={`tab-button ${activeTab === 'configuration' ? 'active' : ''}`}
            onClick={() => setActiveTab('configuration')}
          >
            ⚙️ الإعدادات
          </button>
          <button
            className={`tab-button ${activeTab === 'checklist' ? 'active' : ''}`}
            onClick={() => setActiveTab('checklist')}
          >
            ✅ قائمة الإعداد
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {/* Basic Information */}
              <div>
                <h4 style={{ marginBottom: '1rem', color: 'var(--primary-600)', fontSize: '1rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  المعلومات الأساسية
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>الاسم</div>
                    <div style={{ fontWeight: 500, color: 'var(--gray-900)' }}>{customerData.name || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>البريد الإلكتروني</div>
                    <div style={{ fontWeight: 500, color: 'var(--gray-900)' }}>{customerData.email || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>الهاتف</div>
                    <div style={{ fontWeight: 500, color: 'var(--gray-900)' }}>{customerData.phone || '—'}</div>
                  </div>
                  {customerData.affiliate && (
                    <div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>الشريك (مسوق بالعمولة)</div>
                      <div style={{ fontWeight: 500, color: 'var(--gray-900)' }}>
                        {typeof customerData.affiliate === 'object' && customerData.affiliate !== null
                          ? `${customerData.affiliate.name || ''}${customerData.affiliate.code ? ` (${customerData.affiliate.code})` : ''}`.trim() || '—'
                          : String(customerData.affiliate)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Store URLs */}
              {(customerData.apiUrl || customerData.storeFrontendUrl) && (
                <div>
                  <h4 style={{ marginBottom: '1rem', color: 'var(--primary-600)', fontSize: '1rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                    الروابط
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {customerData.apiUrl && (
                      <div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>API URL</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.875rem', backgroundColor: 'var(--gray-50)', padding: '0.5rem', borderRadius: 'var(--radius-md)', wordBreak: 'break-all' }}>
                          {customerData.apiUrl}
                        </div>
                      </div>
                    )}
                    {customerData.port && (
                      <div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>Port</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.875rem', backgroundColor: 'var(--gray-50)', padding: '0.5rem', borderRadius: 'var(--radius-md)' }}>
                          {customerData.port}
                        </div>
                      </div>
                    )}
                    {customerData.storeFrontendUrl && (
                      <div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>رابط الواجهة الأمامية</div>
                        <a href={customerData.storeFrontendUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)', textDecoration: 'none', fontWeight: 500 }}>
                          {customerData.storeFrontendUrl} ↗
                        </a>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                      <button className="btn btn-primary btn-sm" onClick={handleShowBackendEnv}>
                        ملف .env للخادم
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleShowFrontendEnv}>
                        ملف .env للواجهة
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleShowAgentBackendEnv}>
                        ملف .env لخادم الوكيل
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleShowAgentFrontendEnv}>
                        ملف .env لواجهة الوكيل
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={handleShowWhatsAppMessage}>
                        رسالة واتساب
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* API Token */}
              {apiToken && (
                <div>
                  <h4 style={{ marginBottom: '1rem', color: 'var(--primary-600)', fontSize: '1rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                    رمز API
                  </h4>
                  <div style={{ backgroundColor: 'var(--gray-50)', padding: '1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-200)', position: 'relative' }}>
                    <code style={{ 
                      display: 'block', 
                      fontSize: '0.8125rem', 
                      wordBreak: 'break-all',
                      fontFamily: 'monospace',
                      color: 'var(--gray-900)',
                      marginBottom: '0.75rem',
                      paddingRight: '6rem'
                    }}>
                      {showApiToken ? apiToken : '••••••••'}
                    </code>
                    <div style={{ position: 'absolute', left: '1rem', top: '1rem', display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setShowApiToken(!showApiToken)}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--gray-300)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          color: 'var(--gray-700)',
                          fontWeight: 500
                        }}
                      >
                        {showApiToken ? '👁️ إخفاء' : '👁️ عرض'}
                      </button>
                      <button
                        onClick={handleCopyApiKey}
                        style={{
                          background: apiKeyCopied ? 'var(--success-600)' : 'var(--primary-600)',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          color: 'white',
                          fontWeight: 500,
                          transition: 'background-color 0.2s'
                        }}
                      >
                        {apiKeyCopied ? '✓ تم النسخ' : '📋 نسخ'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div>
            {subscription ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>الباقة</div>
                    <div style={{ fontWeight: 600, fontSize: '1.125rem' }}>
                      {subscription.package === 'basic' ? 'أساسي' :
                       subscription.package === 'standard' ? 'قياسي' :
                       subscription.package === 'premium' ? 'مميز' : subscription.package}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>الحالة</div>
                    <div>
                      {new Date(subscription.endDate) < new Date() ? (
                        <span className="badge badge-danger">منتهي</span>
                      ) : subscription.status === 'active' ? (
                        <span className="badge badge-success">نشط</span>
                      ) : subscription.status === 'cancelled' ? (
                        <span className="badge badge-danger">ملغي</span>
                      ) : (
                        <span className="badge badge-info">قيد الانتظار</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>تاريخ البدء</div>
                    <div style={{ fontWeight: 500 }}>{new Date(subscription.startDate).toLocaleDateString('en-US')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>تاريخ الانتهاء</div>
                    <div style={{ fontWeight: 500 }}>{new Date(subscription.endDate).toLocaleDateString('en-US')}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-title">لا يوجد اشتراك نشط</div>
                <div className="empty-state-text" style={{ marginTop: '1rem' }}>
                  <button className="btn btn-primary" onClick={() => setActiveTab('create-subscription')}>
                    إضافة اشتراك جديد
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Subscription Tab - shown when no subscription */}
        {activeTab === 'create-subscription' && (
          <div>
            <CreateSubscription
              customerId={customer._id}
              onSubscriptionCreated={(newSubscription) => {
                setSubscription(newSubscription);
                setActiveTab('subscription');
                if (onRefresh) onRefresh();
              }}
              onCancel={() => setActiveTab('subscription')}
            />
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div>
            <h3 className="card-title" style={{ marginBottom: '1.5rem' }}>سجل الاستخدام</h3>
            {loading ? (
              <div className="loading">
                <div className="loading-spinner"></div>
                جاري التحميل...
              </div>
            ) : usageLogs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📝</div>
                <div className="empty-state-title">لا يوجد سجل استخدام</div>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>الخدمة</th>
                      <th>التكلفة</th>
                      <th>حالة الاستجابة</th>
                      <th>التاريخ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageLogs.map((log) => (
                      <tr key={log._id}>
                        <td>
                          {log.service === 'AI_IMAGE' ? 'توليد الصور' : 
                           log.service === 'AI_IMAGE_PRO' ? 'توليد الصور Pro' :
                           log.service === 'SMS' ? 'الرسائل النصية' : 
                           log.service === 'OPENAI' ? 'OpenAI' : log.service}
                        </td>
                        <td><strong>{log.cost}</strong></td>
                        <td>
                          <span className={`badge ${log.responseStatus === 200 ? 'badge-success' : 'badge-danger'}`}>
                            {log.responseStatus}
                          </span>
                        </td>
                        <td>{new Date(log.createdAt).toLocaleString('en-US')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div>
            <h3 className="card-title" style={{ marginBottom: '1.5rem' }}>المعاملات</h3>
            {transactions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">💳</div>
                <div className="empty-state-title">لا يوجد معاملات</div>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>النوع</th>
                      <th>نوع الرصيد</th>
                      <th>الخدمة</th>
                      <th>المبلغ</th>
                      <th>الرصيد قبل</th>
                      <th>الرصيد بعد</th>
                      <th>المرجع</th>
                      <th>التاريخ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction) => (
                      <tr key={transaction._id}>
                        <td>
                          <span className={`badge ${transaction.type === 'CREDIT' ? 'badge-success' : 'badge-danger'}`}>
                            {transaction.type === 'CREDIT' ? '➕ إضافة' : '➖ خصم'}
                          </span>
                        </td>
                        <td>
                          <span className="badge badge-info">
                            {transaction.creditType === 'aiCredits' ? 'AI' : 
                             transaction.creditType === 'smsCredits' ? 'SMS' :
                             transaction.creditType === 'openaiBalance' ? 'OpenAI' : transaction.creditType}
                          </span>
                        </td>
                        <td>
                          {transaction.service === 'AI_IMAGE' ? 'توليد الصور' : 
                           transaction.service === 'AI_IMAGE_PRO' ? 'توليد الصور Pro' :
                           transaction.service === 'SMS' ? 'الرسائل النصية' : 
                           transaction.service === 'OPENAI' ? 'OpenAI' :
                           transaction.service === 'MANUAL' ? 'يدوي' : transaction.service}
                        </td>
                        <td><strong>{transaction.amount}</strong></td>
                        <td>{transaction.balanceBefore}</td>
                        <td><strong>{transaction.balanceAfter}</strong></td>
                        <td>
                          {transaction.reference ? (
                            <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                              {transaction.reference}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>—</span>
                          )}
                        </td>
                        <td style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                          {new Date(transaction.createdAt).toLocaleString('en-US')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Financials Tab */}
        {activeTab === 'financials' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>إجمالي الفواتير</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary-600)' }}>
                  {(financialSummary?.totalInvoiced ?? 0).toLocaleString('en-US')} USD
                </div>
              </div>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>المدفوع</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--success)' }}>
                  {(financialSummary?.totalPaid ?? 0).toLocaleString('en-US')} USD
                </div>
              </div>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>المتبقي</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: (financialSummary?.outstanding ?? 0) > 0 ? 'var(--warning)' : 'var(--gray-600)' }}>
                  {(financialSummary?.outstanding ?? 0).toLocaleString('en-US')} USD
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <h3 className="card-title" style={{ margin: 0 }}>الفواتير</h3>
              <button type="button" className="btn btn-primary" onClick={openCreateInvoice}>
                + إنشاء فاتورة
              </button>
            </div>
            {invoices.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📄</div>
                <div className="empty-state-title">لا توجد فواتير</div>
                <div className="empty-state-text">
                  <button type="button" className="btn btn-primary" onClick={openCreateInvoice}>
                    إنشاء فاتورة جديدة
                  </button>
                </div>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>رقم الفاتورة</th>
                      <th>تاريخ الإصدار</th>
                      <th>تاريخ الاستحقاق</th>
                      <th>المبلغ</th>
                      <th>الحالة</th>
                      <th>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv._id}>
                        <td style={{ fontWeight: 600 }}>{inv.invoiceNumber}</td>
                        <td>{inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('en-US') : '—'}</td>
                        <td>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-US') : '—'}</td>
                        <td><strong>{inv.total?.toLocaleString('en-US')} {inv.currency || 'USD'}</strong></td>
                        <td>
                          <select
                            value={inv.status}
                            onChange={(e) => handleStatusChange(inv, e.target.value)}
                            style={{
                              padding: '0.35rem 0.5rem',
                              fontSize: '0.8125rem',
                              borderRadius: 6,
                              border: '1px solid #e2e8f0',
                              backgroundColor: inv.status === 'paid' ? '#dcfce7' : inv.status === 'cancelled' ? '#fee2e2' : inv.status === 'overdue' ? '#fef3c7' : '#f1f5f9',
                              fontWeight: 500,
                              cursor: 'pointer',
                              minWidth: 95,
                            }}
                          >
                            <option value="draft">مسودة</option>
                            <option value="sent">مرسلة</option>
                            <option value="paid">مدفوعة</option>
                            <option value="overdue">متأخرة</option>
                            <option value="cancelled">ملغاة</option>
                          </select>
                        </td>
                        <td>
                          <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: '0.25rem' }} onClick={() => openEditInvoice(inv)}>
                            تعديل
                          </button>
                          <button type="button" className="btn btn-primary btn-sm" style={{ marginLeft: '0.25rem' }} onClick={() => printInvoice(inv)} title="طباعة الفاتورة">
                            🖨️ طباعة
                          </button>
                          {inv.status !== 'cancelled' && (
                            <button type="button" className="btn btn-danger btn-sm" style={{ marginLeft: '0.25rem' }} onClick={() => handleCancelInvoice(inv)}>
                              إلغاء
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
              {/* Basic Information Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  المعلومات الأساسية
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="الاسم" value={customerData.name} />
                  <InfoField label="البريد الإلكتروني" value={customerData.email} />
                  <InfoField label="الهاتف" value={customerData.phone} />
                  {customerData.affiliate && (
                    <InfoField
                      label="الشريك (مسوق بالعمولة)"
                      value={typeof customerData.affiliate === 'object' && customerData.affiliate !== null
                        ? `${customerData.affiliate.name || ''}${customerData.affiliate.code ? ` (${customerData.affiliate.code})` : ''}`.trim() || '—'
                        : String(customerData.affiliate)}
                    />
                  )}
                  <InfoField label="OpenAI Project ID" value={customerData.openaiProjectId} code />
                  {customerData.vps && (
                    <InfoField label="خادم VPS" value={customerData.vps?.ip || customerData.vps} />
                  )}
                  {customerData.createdAt && (
                    <InfoField label="تاريخ الإنشاء" value={new Date(customerData.createdAt).toLocaleString('en-US')} />
                  )}
                  {customerData.updatedAt && (
                    <InfoField label="آخر تحديث" value={new Date(customerData.updatedAt).toLocaleString('en-US')} />
                  )}
                </div>
              </div>


              

              {/* URLs Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  الروابط
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="API URL" value={customerData.apiUrl} url code />
                  <InfoField label="رابط الواجهة الأمامية" value={customerData.storeFrontendUrl} url />
                </div>
              </div>

              

              {/* MongoDB Configuration Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  إعدادات MongoDB
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="MongoDB URI" value={customerData.mongodbUri} code secret copyable />
                  <InfoField label="اسم قاعدة البيانات" value={customerData.mongodbDbName} />
                  <InfoField label="JWT Secret" value={customerData.jwtSecret} code secret copyable />
                  <InfoField label="JWT Expire" value={customerData.jwtExpire} />
                </div>
              </div>

              {/* R2 / Cloudflare Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  إعدادات R2 / Cloudflare
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="Access Key ID" value={customerData.r2AccessKeyId} code secret copyable />
                  <InfoField label="Secret Access Key" value={customerData.r2SecretAccessKey} code secret copyable />
                  <InfoField label="اسم الـ Bucket" value={customerData.r2BucketName} />
                  <InfoField label="Public URL" value={customerData.r2PublicUrl} url />
                  <InfoField label="Endpoint" value={customerData.r2Endpoint} code copyable />
                  <InfoField label="Public Base URL" value={customerData.r2PublicBaseUrl} url />
                </div>
              </div>

              {/* Telegram Configuration Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  إعدادات Telegram
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="Bot Token" value={customerData.telegramBotToken} code secret copyable />
                  <InfoField label="Bot Username" value={customerData.telegramBotUsername} />
                  <InfoField label="Notification Token" value={customerData.telegramNotificationToken} code secret copyable />
                </div>
              </div>

              {/* Admin Configuration Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  بيانات الإدارة الافتراضية
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="البريد الإلكتروني" value={customerData.adminDefaultEmail} email />
                  <InfoField label="كلمة المرور" value={customerData.adminDefaultPassword} secret copyable />
                  <InfoField label="الاسم" value={customerData.adminDefaultName} />
                  <InfoField label="الهاتف" value={customerData.adminDefaultPhone} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Checklist Tab */}
        {activeTab === 'checklist' && (
          <div>
            <SetupChecklist
              customerId={customer._id}
              checklist={customerData.setupChecklist || []}
              onUpdate={(updatedData) => {
                setCustomerData(updatedData);
                if (onRefresh) onRefresh();
              }}
            />
          </div>
        )}
      </div>

      {/* Invoice Create/Edit Modal — Professional design */}
      {invoiceModal.open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1.5rem',
          }}
          onClick={(e) => e.target === e.currentTarget && closeInvoiceModal()}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: 920,
              maxHeight: '92vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#fff',
              borderRadius: 12,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div style={{
              flexShrink: 0,
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8fafc',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#0f172a' }}>
                {invoiceModal.mode === 'create' ? 'إنشاء فاتورة جديدة' : 'تعديل الفاتورة'}
              </h2>
              <button
                type="button"
                onClick={closeInvoiceModal}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  color: '#64748b',
                }}
                aria-label="إغلاق"
              >
                ×
              </button>
            </div>

            {/* Template prompt overlay */}
            {templatePrompt && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'rgba(15, 23, 42, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  padding: '1rem',
                }}
                onClick={(e) => e.target === e.currentTarget && closeTemplatePrompt()}
              >
                <div
                  style={{
                    width: '100%',
                    maxWidth: 400,
                    backgroundColor: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    overflow: 'hidden',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{
                    padding: '1.25rem 1.5rem',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f8fafc',
                  }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>
                      {templatePrompt.template.category === 'plan' ? 'إضافة بند خطة اشتراك' : 'إضافة بند رصيد'}
                    </h3>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={closeTemplatePrompt} style={{ padding: '0.35rem 0.6rem' }}>×</button>
                  </div>
                  <form onSubmit={confirmTemplateAdd} style={{ padding: '1.5rem' }}>
                    <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1.25rem', fontWeight: 500 }}>
                      {templatePrompt.template.label}
                    </p>
                    {templatePrompt.template.category === 'plan' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>تاريخ البدء</label>
                          <input type="date" className="form-control" value={templatePromptForm.startDate} onChange={(e) => setTemplatePromptForm((p) => ({ ...p, startDate: e.target.value }))} required style={{ width: '100%' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>تاريخ الانتهاء</label>
                          <input type="date" className="form-control" value={templatePromptForm.endDate} onChange={(e) => setTemplatePromptForm((p) => ({ ...p, endDate: e.target.value }))} required style={{ width: '100%' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>المبلغ (سعر الاشتراك)</label>
                          <input type="number" className="form-control" min="0" step="0.01" value={templatePromptForm.unitPrice} onChange={(e) => setTemplatePromptForm((p) => ({ ...p, unitPrice: e.target.value }))} placeholder="0" style={{ width: '100%' }} />
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>الكمية</label>
                          <input type="number" className="form-control" min="0.01" step="0.01" value={templatePromptForm.quantity} onChange={(e) => setTemplatePromptForm((p) => ({ ...p, quantity: e.target.value }))} required style={{ width: '100%' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>سعر الوحدة</label>
                          <input type="number" className="form-control" min="0" step="0.01" value={templatePromptForm.unitPrice} onChange={(e) => setTemplatePromptForm((p) => ({ ...p, unitPrice: e.target.value }))} placeholder="0" style={{ width: '100%' }} />
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                      <button type="button" className="btn btn-secondary" onClick={closeTemplatePrompt}>إلغاء</button>
                      <button type="submit" className="btn btn-primary">إضافة البند</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Modal body — scrollable */}
            <form onSubmit={invoiceModal.mode === 'create' ? handleCreateInvoice : handleUpdateInvoice} style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Section: Invoice details */}
                <section>
                  <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
                    تفاصيل الفاتورة
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>تاريخ الاستحقاق</label>
                      <input type="date" className="form-control" value={invoiceForm.dueDate} onChange={(e) => setInvoiceForm((p) => ({ ...p, dueDate: e.target.value }))} required style={{ width: '100%' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>العملة</label>
                      <select
                        className="form-control"
                        value={invoiceForm.currency}
                        onChange={(e) => setInvoiceForm((p) => ({ ...p, currency: e.target.value }))}
                        style={{ width: '100%' }}
                      >
                        {!CURRENCIES.some((c) => c.code === invoiceForm.currency) && invoiceForm.currency && (
                          <option value={invoiceForm.currency}>{invoiceForm.currency}</option>
                        )}
                        {CURRENCIES.map((c) => (
                          <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </section>

                {/* Section: Line items */}
                <section>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                      البنود
                    </h3>
                    <div style={{
                      padding: '0.75rem 1rem',
                      backgroundColor: '#f8fafc',
                      borderRadius: 10,
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      flexWrap: 'wrap',
                    }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#64748b' }}>إضافة بند:</span>
                      <div ref={templateDropdownRef} style={{ position: 'relative' }}>
                        <button
                          type="button"
                          onClick={() => setTemplateDropdownOpen((o) => !o)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#334155',
                            backgroundColor: '#fff',
                            border: '1px solid #cbd5e1',
                            borderRadius: 8,
                            cursor: 'pointer',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          }}
                        >
                          <span style={{ opacity: 0.9 }}>📋</span>
                          من قالب
                          <span style={{ fontSize: '0.65rem', opacity: 0.7, transform: templateDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                        </button>
                        {templateDropdownOpen && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              marginTop: 4,
                              minWidth: 280,
                              backgroundColor: '#fff',
                              borderRadius: 10,
                              boxShadow: '0 10px 40px -10px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
                              overflow: 'hidden',
                              zIndex: 50,
                            }}
                          >
                            <div style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              قوالب البنود
                            </div>
                            <div style={{ padding: '0.35rem 0' }}>
                              <div style={{ padding: '0.25rem 0.75rem', fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', marginTop: '0.35rem' }}>خطط اشتراك</div>
                              {INVOICE_ITEM_TEMPLATES.filter((t) => t.category === 'plan').map((t) => (
                                <button
                                  key={t.id}
                                  type="button"
                                  onClick={() => openTemplatePrompt(t.id)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.6rem',
                                    width: '100%',
                                    padding: '0.6rem 1rem',
                                    border: 'none',
                                    background: 'transparent',
                                    fontSize: '0.875rem',
                                    color: '#334155',
                                    cursor: 'pointer',
                                    textAlign: 'right',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                  <span style={{ fontSize: '1rem', opacity: 0.8 }}>📅</span>
                                  {t.label}
                                </button>
                              ))}
                              <div style={{ padding: '0.25rem 0.75rem', fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', marginTop: '0.5rem', borderTop: '1px solid #f1f5f9' }}>أرصدة</div>
                              {INVOICE_ITEM_TEMPLATES.filter((t) => t.category === 'balance').map((t) => (
                                <button
                                  key={t.id}
                                  type="button"
                                  onClick={() => openTemplatePrompt(t.id)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.6rem',
                                    width: '100%',
                                    padding: '0.6rem 1rem',
                                    border: 'none',
                                    background: 'transparent',
                                    fontSize: '0.875rem',
                                    color: '#334155',
                                    cursor: 'pointer',
                                    textAlign: 'right',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                  <span style={{ fontSize: '1rem', opacity: 0.8 }}>💰</span>
                                  {t.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addInvoiceLine} style={{ padding: '0.5rem 1rem', border: '1px solid #e2e8f0', borderRadius: 8 }}>+ سطر فارغ</button>
                    </div>
                  </div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ maxHeight: 320, overflowY: 'auto', overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: 500 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f8fafc' }}>
                          <tr>
                            <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>الوصف</th>
                            <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', width: 90 }}>الكمية</th>
                            <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', width: 110 }}>سعر الوحدة</th>
                            <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', width: 90 }}>المبلغ</th>
                            <th style={{ padding: '0.75rem 0.5rem', width: 44, borderBottom: '1px solid #e2e8f0' }} />
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceForm.items.map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: idx < invoiceForm.items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                              <td style={{ padding: '0.5rem 1rem' }}>
                                <input type="text" className="form-control" value={row.description} onChange={(e) => updateInvoiceLine(idx, 'description', e.target.value)} placeholder="وصف الصنف" style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }} />
                              </td>
                              <td style={{ padding: '0.5rem 1rem' }}>
                                <input type="number" className="form-control" min="0" step="0.01" value={row.quantity} onChange={(e) => updateInvoiceLine(idx, 'quantity', e.target.value)} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }} />
                              </td>
                              <td style={{ padding: '0.5rem 1rem' }}>
                                <input type="number" className="form-control" min="0" step="0.01" value={row.unitPrice} onChange={(e) => updateInvoiceLine(idx, 'unitPrice', e.target.value)} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }} />
                              </td>
                              <td style={{ padding: '0.5rem 1rem', fontWeight: 600, color: '#0f172a' }}>{(Number(row.quantity) * Number(row.unitPrice)).toLocaleString('en-US')}</td>
                              <td style={{ padding: '0.5rem' }}>
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => removeInvoiceLine(idx)} disabled={invoiceForm.items.length <= 1} style={{ padding: '0.35rem 0.5rem', fontSize: '0.75rem' }}>حذف</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>

                {/* Section: Totals & notes */}
                <section>
                  <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
                    الإجماليات والملاحظات
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>نسبة الضريبة %</label>
                      <input type="number" className="form-control" min="0" max="100" step="0.01" value={invoiceForm.taxRate} onChange={(e) => setInvoiceForm((p) => ({ ...p, taxRate: e.target.value }))} style={{ width: '100%' }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>ملاحظات</label>
                    <textarea className="form-control" rows={2} value={invoiceForm.notes} onChange={(e) => setInvoiceForm((p) => ({ ...p, notes: e.target.value }))} placeholder="اختياري" style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem', fontSize: '0.875rem', resize: 'vertical' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>معلومات الدفع (للعميل — أين وكيف يدفع)</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={invoiceForm.paymentInfo}
                      onChange={(e) => setInvoiceForm((p) => ({ ...p, paymentInfo: e.target.value }))}
                      placeholder={defaultPaymentInfo || 'اتركه فارغاً لاستخدام النص الافتراضي (بنك، رقم حساب، اسم المستفيد، آيبان)'}
                      style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem', fontSize: '0.875rem', resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </div>
                  {invoiceModal.mode === 'edit' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>الحالة</label>
                        <select className="form-control" value={invoiceForm.status} onChange={(e) => setInvoiceForm((p) => ({ ...p, status: e.target.value }))} style={{ width: '100%' }}>
                          <option value="draft">مسودة</option>
                          <option value="sent">مرسلة</option>
                          <option value="paid">مدفوعة</option>
                          <option value="overdue">متأخرة</option>
                          <option value="cancelled">ملغاة</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>المبلغ المدفوع</label>
                        <input type="number" className="form-control" min="0" step="0.01" value={invoiceForm.amountPaid ?? 0} onChange={(e) => setInvoiceForm((p) => ({ ...p, amountPaid: e.target.value }))} style={{ width: '100%' }} />
                      </div>
                    </div>
                  )}
                </section>
              </div>

              {/* Modal footer */}
              <div style={{
                flexShrink: 0,
                padding: '1rem 1.5rem',
                borderTop: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'flex-end',
              }}>
                <button type="button" className="btn btn-secondary" onClick={closeInvoiceModal}>إلغاء</button>
                <button type="submit" className="btn btn-primary" disabled={invoiceSaving}>
                  {invoiceSaving ? 'جاري الحفظ...' : (invoiceModal.mode === 'create' ? 'إنشاء الفاتورة' : 'حفظ التعديلات')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <EnvModal
        isOpen={envModal.isOpen}
        onClose={() => setEnvModal({ isOpen: false, title: '', content: '', variant: '' })}
        title={envModal.title}
        content={envModal.content}
        variant={envModal.variant}
      />
    </div>
  );
};

export default CustomerDetails;
